import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  CreateTemplateFormDTO,
  TemplateDetailDTO,
  TemplateVersionDTO,
  UpdateTemplateFormDTO,
} from "../../domain/contracts";
import type {
  CreateTemplateCategoryDTO,
  TemplateCategoryDTO,
  UpdateTemplateCategoryDTO,
} from "../../domain/contracts/category";
import type {
  DepartmentTemplatesResult,
  TemplateListResult,
} from "../../domain/types";

const baseUrl = `${config.apiUrl}/template`;

function toFormData(input: Record<string, unknown>): FormData {
  const fd = new FormData();
  Object.entries(input).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (v instanceof File) {
      fd.append(k, v);
    } else if (Array.isArray(v)) {
      // Handle array fields like departmentIds
      v.forEach((item) => {
        fd.append(k, String(item));
      });
    } else {
      fd.append(k, String(v));
    }
  });
  return fd;
}

export const templateApi = {
  async listByDepartment(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}/by-department?${params.toString()}`;
    const res = await fetcher.get<DepartmentTemplatesResult>(url);
    return res;
  },

  async list(query: Partial<OffsetPageQueryDTO> = {}) {
    const params = new URLSearchParams();
    if (query.limit) params.set("limit", String(query.limit));
    if (query.offset) params.set("offset", String(query.offset));
    if (query.sort) params.set("sort", JSON.stringify(query.sort));
    if (query.filters) params.set("filters", JSON.stringify(query.filters));

    const url = `${baseUrl}?${params.toString()}`;
    const res = await fetcher.get<TemplateListResult>(url);
    return res;
  },

  async get(id: string) {
    const res = await fetcher.get<TemplateDetailDTO>(`${baseUrl}/${id}`);
    return res;
  },

  async getVersions(id: string) {
    const res = await fetcher.get<{ versions: TemplateVersionDTO[] }>(
      `${baseUrl}/${id}/versions`,
    );
    return res.versions;
  },

  async update(id: string, input: { status: "active" | "deprecated" | "archived" }) {
    const res = await fetcher.patch<{ id: string; status: string }>(
      `${baseUrl}/${id}`,
      input,
    );
    return res;
  },

  async delete(id: string) {
    const res = await fetcher.delete<{ deleted: boolean }>(`${baseUrl}/${id}`);
    return res;
  },

  async create(input: CreateTemplateFormDTO) {
    const formData = toFormData({
      name: input.name,
      categoryId: input.categoryId || undefined,
      description: input.description || undefined,
      usageNotes: input.usageNotes || undefined,
      departmentIds: input.departmentIds || [],
      version: input.version || undefined,
      changelog: input.changelog || undefined,
      keepOldVersionsActive: input.keepOldVersionsActive ?? true,
      fileKey: input.fileKey || undefined,
    });
    const res = await fetcher.postForm<{ id: string }>(baseUrl, formData);
    return res;
  },

  async updateFull(id: string, input: UpdateTemplateFormDTO) {
    const formData = toFormData({
      name: input.name || undefined,
      categoryId: input.categoryId || undefined,
      description: input.description || undefined,
      usageNotes: input.usageNotes || undefined,
      departmentIds: input.departmentIds || [],
      version: input.version || undefined,
      changelog: input.changelog || undefined,
      keepOldVersionsActive: input.keepOldVersionsActive ?? true,
      fileKey: input.fileKey || undefined,
    });
    const res = await fetcher.putForm<{ id: string }>(`${baseUrl}/${id}`, formData);
    return res;
  },

  async getCategories() {
    const res = await fetcher.get<{ categories: Array<{ id: string; name: string; code: string; description: string | null }> }>(
      `${baseUrl}/categories`,
    );
    return res.categories;
  },

  async getCategory(id: string) {
    const res = await fetcher.get<TemplateCategoryDTO>(
      `${baseUrl}/categories/${id}`,
    );
    return res;
  },

  async createCategory(input: CreateTemplateCategoryDTO) {
    const res = await fetcher.post<TemplateCategoryDTO>(
      `${baseUrl}/categories`,
      input,
    );
    return res;
  },

  async updateCategory(id: string, input: UpdateTemplateCategoryDTO) {
    const res = await fetcher.patch<TemplateCategoryDTO>(
      `${baseUrl}/categories/${id}`,
      input,
    );
    return res;
  },

  async deleteCategory(id: string) {
    const res = await fetcher.delete<{ ok: boolean }>(
      `${baseUrl}/categories/${id}`,
    );
    return res;
  },
};
